import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Subject, Subscription, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError, tap } from 'rxjs/operators';

import { HttpEventType } from '@angular/common/http';
import { AppLink } from '../models/app-link';
import { AppLinkService } from '../services/app-link/app-link.service';
import { ProgressBarService } from '../services/progress-bar.service';
import { MessageHandlerService } from '../services/message-handler.service';
import { ERROR_MESSAGES, APK_DATA_DELETED_SUCCESS } from '../common/Constants';

@Component({
  selector: 'app-app-link',
  templateUrl: './app-link.component.html',
  styleUrls: ['./app-link.component.scss'],
})
export class AppLinkComponent implements OnInit, OnDestroy {
  apps: AppLink[] = [];
  loading = false;

  // search & pagination
  searchInput = '';
  pageNo = 0;
  noOfRecordsPerRow = 10;
  rowsPerPageOptions: number[] = [10, 20, 30];
  totalApps = 0;

  // dialog / form
  dialogVisible = false;
  isEditMode = false;
  isSubmitting = false;
  isUploadingFile = false;
  isFileUploadComplete = true;
  uploadProgressBar = false;
  uploadProgressValue = 0;
  uploadedApkKey: string | null = null;
  selectedApp: AppLink | null = null;
  originalApp: AppLink | null = null; // Store original app data to restore on cancel
  fileDeleted = false; // Track if file was deleted in edit mode
  appLinkForm: FormGroup;
  selectedFile: File | null = null;

  private subscriptions: Subscription[] = [];
  private searchSubject = new Subject<string>();
  private refreshSubject = new Subject<void>();
  private uploadProgressIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly appLinkService: AppLinkService,
    private readonly progressBarService: ProgressBarService,
    private readonly messageHandlerService: MessageHandlerService,
    private readonly confirmationService: ConfirmationService,
    private readonly formBuilder: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initSearchAndListPipeline();
    this.getAppLinks();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private initForm(): void {
    this.appLinkForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      appVersion: ['', [Validators.required]],
      file: [null, [Validators.required]],
      isActive: [true],
    });
  }

  get isBusy(): boolean {
    return this.isSubmitting || this.isUploadingFile;
  }

  private initSearchAndListPipeline(): void {
    const search$ = this.searchSubject.pipe(debounceTime(1000), distinctUntilChanged());
    const refresh$ = this.refreshSubject.asObservable();

    this.subscriptions.push(
      merge(search$, refresh$)
        .pipe(
          tap(() => {
            this.loading = true;
            this.progressBarService.setProgressBarVisibility(true);
          }),
          switchMap((searchTerm) => {
            const term = typeof searchTerm === 'string' ? searchTerm.trim() : this.searchInput.trim();

            return this.appLinkService
              .getAll({
                page: this.pageNo + 1,
                limit: this.noOfRecordsPerRow,
                search: term || undefined,
              })
              .pipe(
                catchError((error) => {
                  return of({
                    success: false,
                    errorMessage: error?.errorMessage || error?.message || error,
                  });
                }),
              );
          }),
        )
        .subscribe((res: any) => {
          if (this.isSuccessResponse(res)) {
            // Handle both response formats:
            // Old format: { success: true, content: { data: [...], pagination: {...} } }
            // New format: { success: true, data: [...], totalItems: number, ... }
            const rawData = Array.isArray(res.data)
              ? res.data
              : Array.isArray(res.content?.data)
                ? res.content.data
                : [];

            // Map _id to id for compatibility with AppLink interface
            const data = rawData.map((item: any) => ({
              ...item,
              id: item._id || item.id,
            }));

            this.apps = data || [];
            this.totalApps =
              res.totalItems ??
              res.content?.pagination?.total ??
              res.content?.totalElements ??
              data.length;

            // Pagination safety: if we're on a page that became empty after a delete,
            // step back one page and refetch so the UI updates instead of showing an empty list.
            if (this.apps.length === 0 && this.totalApps > 0 && this.pageNo > 0) {
              this.pageNo = Math.max(0, this.pageNo - 1);
              this.refreshSubject.next();
              return;
            }
          } else {
            this.apps = [];
            this.totalApps = 0;
            if (res?.errorMessage) {
              this.messageHandlerService.generateToastMessage(
                'error',
                ERROR_MESSAGES.COMMUNICATION,
                res.errorMessage,
              );
            }
          }
          this.loading = false;
          this.progressBarService.setProgressBarVisibility(false);
        }),
    );
  }

  private isSuccessResponse(res: any): boolean {
    if (res?.success === true) {
      return true;
    }
    if (res?.statusCode >= 200 && res?.statusCode < 300) {
      return true;
    }
    if (res?.content && !res?.errorMessage) {
      return true;
    }
    // Check if response is the AppLink object directly (has _id, name, apkKey, appVersion)
    if (res?._id && res?.name && res?.apkKey && res?.appVersion) {
      return true;
    }
    return false;
  }

  getAppLinks(): void {
    this.refreshSubject.next();
  }

  onSearchKeyup(): void {
    this.pageNo = 0;
    this.searchSubject.next(this.searchInput.trim());
  }

  paginate(event: any): void {
    // Handle PrimeNG 16 PaginatorState interface with optional properties
    this.pageNo = event.page ?? 0;
    this.noOfRecordsPerRow = event.rows ?? this.noOfRecordsPerRow;
    this.refreshSubject.next();
  }

  openAddDialog(): void {
    this.isEditMode = false;
    this.selectedApp = null;
    this.selectedFile = null;
    this.uploadedApkKey = null;
    this.isFileUploadComplete = false;
    this.uploadProgressBar = false;
    this.uploadProgressValue = 0;
    this.appLinkForm.reset({
      name: '',
      appVersion: '',
      file: null,
      isActive: true,
    });
    this.appLinkForm.get('file')?.setValidators([Validators.required]);
    this.appLinkForm.get('file')?.updateValueAndValidity();
    this.dialogVisible = true;
  }

  openEditDialog(app: AppLink): void {
    this.isEditMode = true;
    // Store a deep copy of the original app to restore on cancel
    this.originalApp = { ...app };
    this.selectedApp = { ...app };
    this.selectedFile = null;
    this.fileDeleted = false;
    this.uploadedApkKey = null;
    this.isFileUploadComplete = true;
    this.uploadProgressBar = false;
    this.uploadProgressValue = 0;

    this.appLinkForm.patchValue({
      name: app.name,
      appVersion: app.appVersion,
      file: null,
      isActive: app.isActive !== undefined ? app.isActive : true,
    });

    this.appLinkForm.get('file')?.clearValidators();
    this.appLinkForm.get('file')?.updateValueAndValidity();

    this.dialogVisible = true;
  }

  onDialogHide(force: boolean = false): void {
    // Prevent user-initiated closing while upload/API is in progress.
    // Allow programmatic close after a successful create/update by passing `force=true`.
    if (this.isBusy && !force) {
      this.dialogVisible = true;
      return;
    }
    // If closing without saving in edit mode, restore original app data
    if (this.isEditMode && this.originalApp) {
      this.selectedApp = { ...this.originalApp };
      this.fileDeleted = false;
    }
    this.dialogVisible = false;
    this.isEditMode = false;
    this.selectedApp = null;
    this.originalApp = null;
    this.isSubmitting = false;
    this.isUploadingFile = false;
    this.isFileUploadComplete = true;
    this.uploadProgressBar = false;
    this.uploadProgressValue = 0;
    this.uploadedApkKey = null;
    this.selectedFile = null;
    this.fileDeleted = false;
    this.clearUploadProgressInterval();
  }

  onFileSelected(event: any): void {
    if (this.isBusy) {
      return;
    }
    const file: File | undefined = event?.target?.files?.[0];
    if (file) {
      this.selectedFile = file;
      this.appLinkForm.get('file')?.setValue(file);
      // Selecting a file implies "replace" (not delete)
      if (this.isEditMode) {
        this.fileDeleted = false;
        if (this.originalApp?.apkKey && this.selectedApp) {
          this.selectedApp.apkKey = this.originalApp.apkKey;
        }
      }
      this.uploadedApkKey = null;
      this.isFileUploadComplete = false;
      this.uploadFile(file);
    } else {
      this.selectedFile = null;
      this.appLinkForm.get('file')?.setValue(null);
      this.uploadedApkKey = null;
      this.isFileUploadComplete = this.isEditMode; // edit mode can be valid without selecting a file
    }
  }

  clearSelectedFile(): void {
    if (this.isBusy) {
      return;
    }

    // If user is clearing a newly-selected file, just reset the staged upload (do NOT mark existing file as deleted)
    if (this.selectedFile) {
      this.selectedFile = null;
      this.uploadedApkKey = null;
      this.isFileUploadComplete = this.isEditMode; // edit mode can proceed without a new file
      this.appLinkForm.get('file')?.setValue(null);
      this.appLinkForm.get('file')?.markAsPristine();
      this.appLinkForm.get('file')?.markAsUntouched();

      // Restore display of the original file in edit mode
      if (this.isEditMode && this.originalApp?.apkKey && this.selectedApp) {
        this.selectedApp.apkKey = this.originalApp.apkKey;
      }
      return;
    }

    // In edit mode, clearing when no new file is selected means "delete existing file"
    if (this.isEditMode) {
      this.fileDeleted = true;
      this.uploadedApkKey = null;
      this.isFileUploadComplete = true;
      if (this.selectedApp) {
        this.selectedApp.apkKey = '';
      }
    }
  }

  onSubmit(): void {
    this.isSubmitting = true;
    this.markFormGroupTouched(this.appLinkForm);

    if (this.isEditMode) {
      // Handle update
      this.handleUpdate();
    } else {
      // Handle create
      if (!this.appLinkForm.valid || !this.selectedFile || !this.uploadedApkKey) {
        this.isSubmitting = false;
        this.messageHandlerService.generateToastMessage(
          'error',
          'Validation Error',
          'Please fill in all required fields and select a file.',
        );
        return;
      }

      this.handleCreate();
    }
  }

  private handleCreate(): void {
    const { name, appVersion, isActive } = this.appLinkForm.value;
    this.progressBarService.setProgressBarVisibility(true);

    const payload = {
      name: String(name || ''),
      apkKey: String(this.uploadedApkKey || ''),
      appVersion: String(appVersion || ''),
      isActive: isActive !== undefined ? Boolean(isActive) : true,
    };

    this.subscriptions.push(
      this.appLinkService.create(payload).subscribe({
        next: (createRes) => {
          // Handle null response from error interceptor (400/403/500 errors)
          if (createRes === null) {
            this.isSubmitting = false;
            this.progressBarService.setProgressBarVisibility(false);
            return;
          }

          if (this.isSuccessResponse(createRes)) {
            this.messageHandlerService.generateToastMessage(
              'success',
              '',
              'App link created successfully',
            );
            this.onDialogHide(true);
            this.pageNo = 0;
            this.getAppLinks();
          } else {
            this.messageHandlerService.generateToastMessage(
              'error',
              ERROR_MESSAGES.COMMUNICATION,
              createRes?.errorMessage || createRes?.message || 'Failed to create app link',
            );
          }
          this.isSubmitting = false;
          this.progressBarService.setProgressBarVisibility(false);
        },
        error: (error) => {
          this.handleError(error);
        },
      }),
    );
  }

  private handleUpdate(): void {
    if (!this.selectedApp?.id) {
      this.isSubmitting = false;
      this.messageHandlerService.generateToastMessage(
        'error',
        'Validation Error',
        'App ID is missing.',
      );
      return;
    }

    if (!this.appLinkForm.valid) {
      this.isSubmitting = false;
      this.messageHandlerService.generateToastMessage(
        'error',
        'Validation Error',
        'Please fill in all required fields.',
      );
      return;
    }

    const { name, appVersion, isActive } = this.appLinkForm.value;
    const appId = this.selectedApp.id;

    // If a new file was selected, it should already be uploaded
    if (this.selectedFile && !this.uploadedApkKey) {
      this.isSubmitting = false;
      this.messageHandlerService.generateToastMessage(
        'error',
        ERROR_MESSAGES.COMMUNICATION,
        'Please wait for the file upload to complete.',
      );
      return;
    }

    let apkKey: string | null = null;
    if (this.selectedFile && this.uploadedApkKey) {
      apkKey = this.uploadedApkKey;
    } else if (this.fileDeleted) {
      apkKey = null;
    } else if (this.originalApp?.apkKey) {
      apkKey = this.originalApp.apkKey;
    } else if (this.selectedApp?.apkKey) {
      apkKey = this.selectedApp.apkKey;
    }

    this.performUpdate(appId, {
      name: String(name || ''),
      apkKey: apkKey !== null ? String(apkKey) : null,
      appVersion: String(appVersion || ''),
      isActive: isActive !== undefined ? Boolean(isActive) : true,
      id: appId,
    });
  }

  private clearUploadProgressInterval(): void {
    if (this.uploadProgressIntervalId) {
      clearInterval(this.uploadProgressIntervalId);
      this.uploadProgressIntervalId = null;
    }
  }

  private resetUploadState(): void {
    this.clearUploadProgressInterval();
    this.isUploadingFile = false;
    this.isFileUploadComplete = this.isEditMode; // edit mode can proceed without uploading a file
    this.uploadProgressBar = false;
    this.uploadProgressValue = 0;
    this.uploadedApkKey = null;
  }

  private uploadFile(file: File): void {
    this.resetUploadState();

    this.isUploadingFile = true;
    this.isFileUploadComplete = false;
    this.uploadProgressBar = true;
    this.uploadProgressValue = 0;
    this.progressBarService.setProgressBarVisibility(true);

    const formData = new FormData();
    formData.append('file', file);

    this.subscriptions.push(
      this.appLinkService.uploadApkWithProgress(formData).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.uploadProgressValue = event.total
              ? Math.round(100 * event.loaded / event.total)
              : 0;
          } else if (event.type === HttpEventType.Response) {
            const uploadRes = event.body;

            if (uploadRes === null) {
              this.resetUploadState();
              this.progressBarService.setProgressBarVisibility(false);
              this.selectedFile = null;
              this.appLinkForm.get('file')?.setValue(null);
              return;
            }

            const fileName = uploadRes?.content?.fileName;
            if (!fileName) {
              this.resetUploadState();
              this.progressBarService.setProgressBarVisibility(false);
              this.selectedFile = null;
              this.appLinkForm.get('file')?.setValue(null);
              this.messageHandlerService.generateToastMessage(
                'error',
                ERROR_MESSAGES.COMMUNICATION,
                'Unable to retrieve file name from upload response.',
              );
              return;
            }

            this.uploadProgressValue = 100;
            this.uploadProgressBar = false;
            this.isUploadingFile = false;
            this.isFileUploadComplete = true;
            this.uploadedApkKey = String(fileName);
            this.progressBarService.setProgressBarVisibility(false);
          }
        },
        error: (error) => {
          this.resetUploadState();
          this.progressBarService.setProgressBarVisibility(false);
          this.selectedFile = null;
          this.appLinkForm.get('file')?.setValue(null);
          this.messageHandlerService.generateToastMessage(
            'error',
            ERROR_MESSAGES.COMMUNICATION,
            error?.errorMessage || error?.message || error,
          );
        },
      }),
    );
  }

  private performUpdate(appId: string | number, payload: { name: string; apkKey: string | null; appVersion: string; isActive: boolean; id: string | number }): void {
    this.progressBarService.setProgressBarVisibility(true);

    this.subscriptions.push(
      this.appLinkService.update(appId, payload).subscribe({
        next: (updateRes) => {
          if (updateRes === null) {
            this.isSubmitting = false;
            this.progressBarService.setProgressBarVisibility(false);
            return;
          }

          if (this.isSuccessResponse(updateRes)) {
            this.messageHandlerService.generateToastMessage(
              'success',
              '',
              'App link updated successfully',
            );
            this.onDialogHide(true);
            this.pageNo = 0;
            this.getAppLinks();
          } else {
            this.messageHandlerService.generateToastMessage(
              'error',
              ERROR_MESSAGES.COMMUNICATION,
              updateRes?.errorMessage || updateRes?.message || 'Failed to update app link',
            );
          }
          this.isSubmitting = false;
          this.progressBarService.setProgressBarVisibility(false);
        },
        error: (error) => {
          this.handleError(error);
        },
      }),
    );
  }

  private handleError(error: any): void {
    const errorMessage = error?.errorMessage || error?.message || error;

    // Swallow technical RxJS error caused by interceptor returning `null`
    // ("You provided 'null' where a stream was expected"), since the
    // ErrorInterceptor already showed a user-facing toast for that case.
    if (
      typeof errorMessage === 'string' &&
      errorMessage.includes("You provided 'null' where a stream was expected")
    ) {
      this.isSubmitting = false;
      this.progressBarService.setProgressBarVisibility(false);
      return;
    }

    // Check if error status is 403/400/500 - interceptor already handled it
    if (error?.status === 403 || error?.status === 400 || error?.status === 500) {
      this.isSubmitting = false;
      this.progressBarService.setProgressBarVisibility(false);
      return;
    }

    this.isSubmitting = false;
    this.progressBarService.setProgressBarVisibility(false);
    this.messageHandlerService.generateToastMessage(
      'error',
      ERROR_MESSAGES.COMMUNICATION,
      errorMessage,
    );
  }

  onRowClick(app: AppLink): void {
    this.openEditDialog(app);
  }

  onDelete(app: AppLink): void {
    if (!app?.id && app?.id !== 0) {
      return;
    }

    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this app?',
      header: 'Delete App',
      icon: 'pi pi-trash',
      acceptButtonStyleClass: 'btn-delete',
      accept: () => {
        this.progressBarService.setProgressBarVisibility(true);
        this.subscriptions.push(
          this.appLinkService.delete(app.id as string | number).subscribe({
            next: (res) => {
              // Handle null/undefined/empty response - DELETE API returns 200 with empty body
              // If we reach the next callback, it means the request was successful (200 status)
              if (res === null || res === undefined || (typeof res === 'object' && Object.keys(res || {}).length === 0)) {
                // Empty response with 200 status means success for DELETE operations
                this.messageHandlerService.generateToastMessage(
                  'success',
                  '',
                  APK_DATA_DELETED_SUCCESS,
                );
                this.getAppLinks();
                this.progressBarService.setProgressBarVisibility(false);
                return;
              }

              if (this.isSuccessResponse(res)) {
                this.messageHandlerService.generateToastMessage(
                  'success',
                  '',
                  APK_DATA_DELETED_SUCCESS,
                );
                this.getAppLinks();
              } else {
                this.messageHandlerService.generateToastMessage(
                  'error',
                  ERROR_MESSAGES.COMMUNICATION,
                  res?.errorMessage || res?.message || 'Failed to delete app',
                );
              }
              this.progressBarService.setProgressBarVisibility(false);
            },
            error: (error) => {
              const errorMessage = error?.errorMessage || error?.message || error;

              // Swallow technical RxJS error caused by interceptor returning `null`
              // ("You provided 'null' where a stream was expected"), since the
              // ErrorInterceptor already showed a user-facing toast for that case.
              if (
                typeof errorMessage === 'string' &&
                errorMessage.includes("You provided 'null' where a stream was expected")
              ) {
                this.progressBarService.setProgressBarVisibility(false);
                return;
              }

              // Check if error status is 403/400/500 - interceptor already handled it
              if (error?.status === 403 || error?.status === 400 || error?.status === 500) {
                this.progressBarService.setProgressBarVisibility(false);
                return;
              }

              this.progressBarService.setProgressBarVisibility(false);
              this.messageHandlerService.generateToastMessage(
                'error',
                ERROR_MESSAGES.COMMUNICATION,
                errorMessage,
              );
            },
          }),
        );
      },
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
    });
  }
}


